import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconCode, IconCodeOff } from '@rossum/ui/icons/tabler';
import { Button, Stack, SvgIcon } from '@rossum/ui/material';
import { chatStore } from '../../../../../chat/hooks/chatStore';
import { useAgentDrawer } from '../../../../../chat/hooks/useAgentDrawer';
import { getFieldDraft } from '../getFieldDraft';
import { InlineLookupComposer } from '../InlineLookupComposer';
import type { LookupResult } from '../lookupResult';
import { AggregationSummary } from './AggregationSummary';
import { DatasetPicker } from './DatasetPicker';
import { MissingInfoEmptyState } from './MissingInfoEmptyState';
import type { LookupEditorProps } from './types';

/**
 * The agentic lookup editor: the first suggestion is composed inline instead of behind a modal, and
 * an agent writes the configuration.
 *
 * It registers itself with the chat store while mounted: the drawer is in another React tree
 * branch, so that is the only way a configuration offered there can reach this form.
 *
 * Editing an existing cascade is a follow-up: the request shape already carries the tier it is
 * scoped to, but nothing drives it yet, so the edit buttons stay inert.
 */
export const AgentLookupEditor = ({
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  lookupLabel,
  isRequiredInfoMissing,
  table,
  tables,
  columnOptions,
  summaryPayload,
  summaryValue,
  isSourceEditorOpen,
  onToggleSourceEditor,
}: LookupEditorProps) => {
  const intl = useIntl();
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(
    new Set()
  );
  const { openOnField, isLoading: isOpeningAgent } = useAgentDrawer();

  // Same application as the legacy editor: the suggester reports a configuration and nothing more.
  const applyResult = useCallback(
    (result: LookupResult) => {
      const next = {
        ...value,
        dataset: result.dataset || value.dataset,
        queries: result.queries,
        variables: result.variables,
      };
      onChange(next);
      return next;
    },
    [onChange, value]
  );

  useEffect(() => {
    chatStore
      .getState()
      .setLookupTarget({ fieldSchemaId: lookupSchemaId, apply: applyResult });

    return () => chatStore.getState().setLookupTarget(null);
    // Re-registers as the user types, so an accepted configuration merges onto the form as it
    // stands rather than a snapshot from mount.
  }, [lookupSchemaId, applyResult]);

  // Array.isArray: the stored configuration arrives through a cast, so it may not be a list at all.
  const hasSomeQueries =
    Array.isArray(value.queries) && value.queries.length > 0;

  const sourceCodeLabel = isSourceEditorOpen
    ? intl.formatMessage({ id: 'features.formulas.hideSourceCode' })
    : intl.formatMessage({ id: 'features.formulas.showSourceCode' });

  return (
    <>
      {isRequiredInfoMissing ? (
        <MissingInfoEmptyState
          lookupLabel={lookupLabel}
          lookupSchemaId={lookupSchemaId}
        />
      ) : hasSomeQueries ? (
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <DatasetPicker
              tables={tables}
              value={table}
              dataset={value.dataset}
              onChange={dataset => onChange({ ...value, dataset })}
            />
            <Button
              variant="contained"
              color="aurora"
              disabled={isOpeningAgent}
              onClick={() =>
                openOnField(
                  getFieldDraft({
                    fieldSchemaId: lookupSchemaId,
                    label: lookupLabel,
                    schemaContent,
                    configuration: value,
                  })
                )
              }
            >
              {intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.editAll',
              })}
            </Button>
          </Stack>
          <AggregationSummary
            disabled={value.dataset === ''}
            fieldSchemaId={summaryPayload.fieldSchemaId}
            schemaContent={summaryPayload.schemaContent}
            onChange={queriesAndVariables =>
              onChange({ ...value, ...queriesAndVariables })
            }
            value={summaryValue}
            dataset={value.dataset}
            datasetName={table?.metadata?.name}
            expandedQueries={expandedQueries}
            onExpandedQueriesChange={setExpandedQueries}
            onQueryEdit={() => {}}
          />
        </Stack>
      ) : (
        <InlineLookupComposer
          schemaContent={schemaContent}
          dataset={value.dataset}
          setDataset={dataset => onChange({ ...value, dataset })}
          datasetColumns={columnOptions}
          fieldSchemaId={lookupSchemaId}
          label={lookupLabel}
          onResult={applyResult}
        />
      )}
      <Stack direction="row" spacing={2} justifyContent="start">
        <Button
          variant="text"
          color="secondary"
          startIcon={
            <SvgIcon fontSize="small">
              {isSourceEditorOpen ? <IconCodeOff /> : <IconCode />}
            </SvgIcon>
          }
          onClick={onToggleSourceEditor}
        >
          {sourceCodeLabel}
        </Button>
      </Stack>
    </>
  );
};
